import { useCallback, useEffect, useRef, useState } from 'react';

import { useAppDispatch, useAppStore } from 'app-components/hooks/redux';

import * as receiptsSelectors from './receiptsSelectors';
import { receiptsActions } from './receiptsSlice';
import type { ReceiptCenterQueries, ReceiptPageQuery, ReceiptReviewState } from './receiptsTypes';

export const receiptListQueryKey = (query: ReceiptPageQuery): string =>
  `${query.pageSize}:${query.sort.field}:${query.sort.direction}`;

type Operation = { cancelled: boolean; appending: boolean; abort?: () => void };
type PageWindow = {
  key: string;
  revision: number;
  pages: string[][];
  query: ReceiptPageQuery;
  ended: boolean;
  operation?: Operation;
  interrupted: boolean;
  refreshFailed: boolean;
};
const createWindow = (query: ReceiptPageQuery): PageWindow => ({
  key: receiptListQueryKey(query),
  revision: query.revision,
  pages: [],
  query: { ...query, page: 0, ids: [], loaded: false, isLoading: false, error: null },
  ended: false,
  interrupted: false,
  refreshFailed: false,
});

export type ReceiptInfinitePages = {
  pages: ReceiptCenterQueries['pages'];
  loadingMore: Record<ReceiptReviewState, boolean>;
  loadMore: (state: ReceiptReviewState) => void;
  retryPage: (state: ReceiptReviewState) => void;
};

/** Stores only IDs from explicitly fetched pages; entities and request acceptance remain in core. */
export const useReceiptInfinitePages = (
  enabled: boolean,
  focused: boolean,
  center: ReceiptCenterQueries,
): ReceiptInfinitePages => {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const windows = useRef<Record<ReceiptReviewState, PageWindow>>({
    NEEDS_REVIEW: createWindow(center.pages.NEEDS_REVIEW),
    REVIEWED: createWindow(center.pages.REVIEWED),
    PROCESSING_OR_ERROR: createWindow(center.pages.PROCESSING_OR_ERROR),
  });
  const [, redraw] = useState(0);
  const publish = useCallback((): void => redraw((value) => value + 1), []);

  const fetchPages = useCallback(
    async (state: ReceiptReviewState, refresh: boolean): Promise<void> => {
      const slot = windows.current[state];
      if (slot.operation) return;
      const source = receiptsSelectors.selectReceiptPageQuery(store.getState(), state);
      const key = receiptListQueryKey(source);
      const revision = source.revision;
      const operation: Operation = { cancelled: false, appending: !refresh };
      slot.operation = operation;
      slot.interrupted = false;
      slot.revision = revision;
      slot.query = {
        ...slot.query,
        revision,
        loaded: refresh ? false : slot.query.loaded,
        isLoading: true,
        error: null,
      };
      publish();
      const pages = refresh ? [] : slot.pages.slice();
      // Refresh only the range the user has already fetched. Never walk the collection.
      const firstPage = refresh ? 0 : pages.length;
      const lastPage = refresh ? Math.max(0, slot.pages.length - 1) : firstPage;
      let total = slot.query.total;
      let ended = false;
      for (let page = firstPage; page <= lastPage; page++) {
        const request = dispatch(
          receiptsActions.fetchReceiptPage({ reviewState: state, page, pageSize: source.pageSize, sort: source.sort }),
        );
        operation.abort = request.abort;
        let superseded = false;
        const unsubscribe = store.subscribe(() => {
          const pendingId = receiptsSelectors.selectReceiptPageQuery(store.getState(), state).requestId;
          if (pendingId && pendingId !== request.requestId) {
            superseded = true;
            request.abort();
          }
        });
        const response = await request;
        unsubscribe();
        const current = receiptsSelectors.selectReceiptPageQuery(store.getState(), state);
        if (
          superseded ||
          operation.cancelled ||
          slot.operation !== operation ||
          current.revision !== revision ||
          receiptListQueryKey(current) !== key ||
          current.requestId
        ) {
          if (!operation.cancelled && slot.operation === operation) {
            slot.operation = undefined;
            slot.interrupted = true;
            slot.query = { ...slot.query, isLoading: false };
            publish();
          }
          return;
        }
        if (receiptsActions.fetchReceiptPage.rejected.match(response)) {
          slot.refreshFailed = refresh;
          slot.operation = undefined;
          slot.query = { ...slot.query, isLoading: false, error: response.error };
          publish();
          return;
        }
        if (!receiptsActions.fetchReceiptPage.fulfilled.match(response)) return;
        total = response.payload.totalElements;
        const ids = response.payload.resources.filter((receipt) => !receipt.isDeleted).map((receipt) => receipt.id);
        pages[page] = ids;
        ended = ids.length === 0 || (page + 1) * source.pageSize >= total;
        if (ended) break;
      }
      slot.pages = pages;
      slot.revision = revision;
      slot.ended = ended;
      slot.refreshFailed = false;
      slot.operation = undefined;
      slot.query = {
        ...source,
        page: Math.max(0, pages.length - 1),
        ids: [...new Set(pages.flat())],
        total,
        loaded: true,
        isLoading: false,
        error: null,
        requestId: undefined,
      };
      publish();
    },
    [dispatch, store, publish],
  );

  const activate = useCallback(
    (state: ReceiptReviewState, force = false): (() => void) | undefined => {
      if (!enabled || !focused) return;
      const source = receiptsSelectors.selectReceiptPageQuery(store.getState(), state);
      let slot = windows.current[state];
      if (slot.key !== receiptListQueryKey(source)) {
        slot = createWindow(source);
        windows.current[state] = slot;
      }
      if (force || !slot.query.loaded || slot.revision !== source.revision || slot.interrupted) {
        void fetchPages(state, true);
      }
      return () => {
        const operation = slot.operation;
        if (!operation) return;
        operation.cancelled = true;
        operation.abort?.();
        slot.operation = undefined;
        slot.interrupted = true;
        slot.query = { ...slot.query, isLoading: false };
      };
    },
    [enabled, focused, fetchPages, store],
  );
  const active = center.activeReviewState;
  const activeQuery = center.pages[active];
  const activeKey = receiptListQueryKey(activeQuery);
  // Query revisions and sort keys invalidate the bounded fetched range.
  useEffect(() => activate(active), [activate, active, activeKey, activeQuery.revision]);
  const processing = center.pages.PROCESSING_OR_ERROR;
  const processingKey = receiptListQueryKey(processing);
  // Summary changes also rediscover processing/error rows.
  useEffect(
    () => activate('PROCESSING_OR_ERROR', true),
    [activate, processingKey, processing.revision, center.summary?.processing, center.summary?.processingErrors],
  );

  const requestMore = (state: ReceiptReviewState, retry: boolean): void => {
    if (!enabled || !focused || (state !== 'PROCESSING_OR_ERROR' && state !== active)) return;
    const slot = windows.current[state];
    const source = receiptsSelectors.selectReceiptPageQuery(store.getState(), state);
    if (
      slot.operation ||
      (!slot.query.loaded && !retry) ||
      slot.key !== receiptListQueryKey(source) ||
      slot.revision !== source.revision ||
      (!retry && slot.query.error)
    )
      return;
    if ((retry && slot.refreshFailed) || slot.interrupted) void fetchPages(state, true);
    else if (!slot.ended) void fetchPages(state, false);
  };
  const getQuery = (state: ReceiptReviewState): ReceiptPageQuery => {
    const source = center.pages[state];
    const slot = windows.current[state];
    return slot.key === receiptListQueryKey(source) ? slot.query : createWindow(source).query;
  };
  return {
    pages: {
      NEEDS_REVIEW: getQuery('NEEDS_REVIEW'),
      REVIEWED: getQuery('REVIEWED'),
      PROCESSING_OR_ERROR: getQuery('PROCESSING_OR_ERROR'),
    },
    loadingMore: {
      NEEDS_REVIEW: windows.current.NEEDS_REVIEW.operation?.appending === true,
      REVIEWED: windows.current.REVIEWED.operation?.appending === true,
      PROCESSING_OR_ERROR: windows.current.PROCESSING_OR_ERROR.operation?.appending === true,
    },
    loadMore: (state) => requestMore(state, false),
    retryPage: (state) => requestMore(state, true),
  };
};
